import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cpGetLineOABroadcastHistory,
  cpGetLineOABroadcastQuota,
  cpSendLineOABroadcast,
  cpScheduleLineOABroadcast,
  cpGetLineOABroadcastScheduled,
  cpCancelLineOABroadcast,
} from '@/lib/api/controlPlaneClient'
import type { BroadcastMedia } from '@/lib/api/controlPlaneLineOAClient'
import { useAuthStore } from '@/lib/store/authStore'

export const LINE_OA_BROADCAST_HISTORY_KEY = ['line-oa-broadcast-history'] as const
export const LINE_OA_BROADCAST_QUOTA_KEY = ['line-oa-broadcast-quota'] as const
export const LINE_OA_BROADCAST_SCHEDULED_KEY = ['line-oa-broadcast-scheduled'] as const

export function useLineOABroadcastHistory(options?: { enabled?: boolean }) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery({
    queryKey: [...LINE_OA_BROADCAST_HISTORY_KEY, activeStorefrontKey],
    queryFn: () => cpGetLineOABroadcastHistory(),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useLineOABroadcastQuota(options?: { enabled?: boolean }) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery({
    queryKey: [...LINE_OA_BROADCAST_QUOTA_KEY, activeStorefrontKey],
    queryFn: cpGetLineOABroadcastQuota,
    staleTime: 1 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useSendBroadcast() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      messageType: string
      textContent?: string
      recipientCount?: number
      media?: BroadcastMedia
    }) =>
      cpSendLineOABroadcast(
        params.messageType,
        params.textContent,
        params.recipientCount,
        params.media
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_OA_BROADCAST_HISTORY_KEY })
      queryClient.invalidateQueries({ queryKey: LINE_OA_BROADCAST_QUOTA_KEY })
    },
  })
}

export function useScheduleBroadcast() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      messageType: string
      scheduledAt: string
      textContent?: string
      imageFile?: File
      recipientCount?: number
    }) =>
      cpScheduleLineOABroadcast(
        params.messageType,
        params.scheduledAt,
        params.textContent,
        params.imageFile,
        params.recipientCount
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_OA_BROADCAST_SCHEDULED_KEY })
    },
  })
}

export function useLineOAScheduledBroadcasts(options?: { enabled?: boolean }) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery({
    queryKey: [...LINE_OA_BROADCAST_SCHEDULED_KEY, activeStorefrontKey],
    queryFn: cpGetLineOABroadcastScheduled,
    staleTime: 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCancelScheduledBroadcast() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => cpCancelLineOABroadcast(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_OA_BROADCAST_SCHEDULED_KEY })
    },
  })
}
